/** Ancho de captura alineado con PrintReportPage / diálogo xl: evita recorte lateral en PDF. */
export const REPORT_PDF_CAPTURE_WIDTH = 1536;

/** Espera a que heatmaps / densidad sobre imagen terminen de dibujar el fondo antes de capturar reportes. */
export function waitForReportChartVisuals(root, { timeoutMs = 20000, pollMs = 150 } = {}) {
  if (!root) return Promise.resolve();
  return new Promise((resolve) => {
    const deadline = Date.now() + timeoutMs;
    const tick = () => {
      const pending = root.querySelectorAll('[data-chart-needs-bg="true"]:not([data-chart-visual-ready="true"])');
      if (pending.length === 0 || Date.now() >= deadline) {
        resolve();
        return;
      }
      setTimeout(tick, pollMs);
    };
    tick();
  });
}

function measureDashboardCaptureSize(element) {
  if (!element) return { width: 800, height: 600 };
  const grid = element.querySelector('.react-grid-layout');
  if (!grid) {
    return {
      width: Math.max(element.scrollWidth, element.offsetWidth),
      height: Math.max(element.scrollHeight, element.offsetHeight),
    };
  }

  const gridRect = grid.getBoundingClientRect();
  let maxRight = grid.offsetWidth;
  let maxBottom = grid.offsetHeight;
  grid.querySelectorAll('.react-grid-item').forEach((item) => {
    maxRight = Math.max(maxRight, item.offsetLeft + item.offsetWidth);
    maxBottom = Math.max(maxBottom, item.offsetTop + item.offsetHeight);
  });
  grid.querySelectorAll('canvas, svg, table').forEach((node) => {
    const rect = node.getBoundingClientRect();
    maxRight = Math.max(maxRight, Math.ceil(rect.right - gridRect.left));
    maxBottom = Math.max(maxBottom, Math.ceil(rect.bottom - gridRect.top));
  });

  const width = Math.max(Math.ceil(maxRight), grid.scrollWidth, element.scrollWidth, element.offsetWidth);
  const height = Math.max(
    Math.ceil(maxBottom),
    grid.scrollHeight,
    element.scrollHeight,
    element.offsetHeight
  );
  return { width, height };
}

// Solo se libera el eje horizontal: el vertical (overflow-y:hidden) es lo que mantiene cada
// tile del grid dentro de su alto asignado (ej. WeatherWidget, KPI). Liberar tambien overflow-y
// aqui hacia que el contenido de un tile se dibujara encima del tile siguiente en la captura
// (ej. "Tiempo Santiago de Chile" superpuesto con "ultima lectura").
function unlockOverflowInClone(clonedEl) {
  if (!clonedEl) return;
  clonedEl.style.overflowX = 'visible';
  clonedEl.querySelectorAll('*').forEach((node) => {
    node.style.overflowX = 'visible';
  });
}

/**
 * react-grid-layout ubica los tiles con transform: translate(x, y) y el tile tiene
 * overflow: hidden. html2canvas calcula el recorte del overflow sin aplicar la
 * traslación, así que todo tile que no esté en (0,0) sale vacío o cortado.
 * En el clon se reemplaza el translate por left/top absolutos.
 */
function flattenGridTransformsInClone(clonedEl) {
  const win = clonedEl.ownerDocument?.defaultView;
  clonedEl.querySelectorAll('.react-grid-item').forEach((item) => {
    const inline = item.style.transform || '';
    let x = null;
    let y = null;
    const m = inline.match(/translate(?:3d)?\(\s*(-?[\d.]+)px\s*,\s*(-?[\d.]+)px/i);
    if (m) {
      x = parseFloat(m[1]);
      y = parseFloat(m[2]);
    } else if (win) {
      const computed = win.getComputedStyle(item).transform;
      const mm = computed && computed.match(/matrix\(([^)]+)\)/);
      if (mm) {
        const parts = mm[1].split(',').map((v) => parseFloat(v));
        x = parts[4];
        y = parts[5];
      }
    }
    if (x == null || y == null || Number.isNaN(x) || Number.isNaN(y)) return;
    item.style.transform = 'none';
    item.style.webkitTransform = 'none';
    item.style.position = 'absolute';
    item.style.left = `${x}px`;
    item.style.top = `${y}px`;
  });
}

function pushStyle(restore, el, prop, value) {
  if (!el) return;
  restore.push({ el, prop, value: el.style[prop] });
  el.style[prop] = value;
}

/**
 * El diálogo del preview recorta con overflow/max-height al alto y ancho de la
 * ventana. html2canvas pinta solo esa ventana, así que el PDF queda igual a lo
 * visible. Se sueltan esos límites para que el grid mida su tamaño real.
 */
function prepareCaptureLayout(element, targetWidth) {
  const restore = [];
  const apply = (el, prop, value) => pushStyle(restore, el, prop, value);

  let node = element.parentElement;
  while (node && node !== document.documentElement) {
    apply(node, 'overflow', 'visible');
    apply(node, 'overflowX', 'visible');
    apply(node, 'overflowY', 'visible');
    apply(node, 'maxHeight', 'none');
    apply(node, 'height', 'auto');
    node = node.parentElement;
  }
  apply(document.documentElement, 'overflow', 'visible');
  apply(document.body, 'overflow', 'visible');

  apply(element, 'width', `${targetWidth}px`);
  apply(element, 'minWidth', `${targetWidth}px`);
  apply(element, 'maxWidth', 'none');
  apply(element, 'height', 'auto');
  apply(element, 'overflow', 'visible');
  apply(element, 'overflowX', 'visible');
  apply(element, 'overflowY', 'visible');
  apply(element, 'boxSizing', 'border-box');

  const scroll = element.querySelector('.chart-grid-scroll');
  if (scroll) {
    apply(scroll, 'width', `${targetWidth}px`);
    apply(scroll, 'minWidth', `${targetWidth}px`);
    apply(scroll, 'maxWidth', 'none');
    apply(scroll, 'height', 'auto');
    apply(scroll, 'overflow', 'visible');
    apply(scroll, 'overflowX', 'visible');
    apply(scroll, 'overflowY', 'visible');
    apply(scroll, 'flex', '0 0 auto');
  }

  return () => {
    for (let i = restore.length - 1; i >= 0; i -= 1) {
      const { el, prop, value } = restore[i];
      el.style[prop] = value;
    }
  };
}

/**
 * Captura un dashboard (contenedor del ChartGrid) para PDF con html2canvas.
 * Fuerza ancho de impresión y evita recorte lateral por overflow:hidden del grid/diálogo.
 */
export async function captureDashboardForPdf(
  element,
  html2canvas,
  { scale = 1.5, targetWidth = REPORT_PDF_CAPTURE_WIDTH } = {}
) {
  if (!element) throw new Error('No hay elemento para capturar');

  const restoreLayout = prepareCaptureLayout(element, targetWidth);
  try {
    window.dispatchEvent(new Event('resize'));
    // ResizeObserver del ChartGrid reflowea el grid al ancho real; sin esta espera
    // html2canvas recorta al viewport del diálogo.
    await new Promise((r) => setTimeout(r, 700));

    const rect = element.getBoundingClientRect();
    const docLeft = Math.max(0, rect.left + window.pageXOffset);
    const docTop = Math.max(0, rect.top + window.pageYOffset);
    const { width, height } = measureDashboardCaptureSize(element);
    const captureW = Math.max(Math.ceil(width), Math.ceil(rect.width), targetWidth, element.scrollWidth);
    const captureH = Math.max(Math.ceil(height), Math.ceil(rect.height), element.scrollHeight, 1);
    // La ventana de html2canvas es el viewport del browser. Si el dashboard empieza
    // más abajo/a la derecha (título del diálogo, márgenes), hay que incluir ese offset
    // o se corta justo lo que no entra en pantalla.
    const windowWidth = Math.ceil(docLeft + captureW);
    const windowHeight = Math.ceil(docTop + captureH);

    return await html2canvas(element, {
      scale,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
      width: captureW,
      height: captureH,
      windowWidth,
      windowHeight,
      scrollX: 0,
      scrollY: 0,
      onclone: (doc, clonedEl) => {
        // Los ancestros (DialogContent con scroll, Paper del diálogo) recortan la captura a lo
        // visible en pantalla; se saca el dashboard a <body> para que no quede dentro de ellos.
        doc.documentElement.style.overflow = 'visible';
        doc.body.style.overflow = 'visible';
        doc.body.style.margin = '0';
        doc.body.appendChild(clonedEl);
        Object.assign(clonedEl.style, {
          position: 'absolute',
          left: '0px',
          top: '0px',
          margin: '0',
          zIndex: '2147483647',
          backgroundColor: '#ffffff',
          transform: 'none',
        });
        clonedEl.style.width = `${captureW}px`;
        clonedEl.style.minWidth = `${captureW}px`;
        clonedEl.style.maxWidth = 'none';
        clonedEl.style.overflow = 'visible';
        flattenGridTransformsInClone(clonedEl);
        unlockOverflowInClone(clonedEl);
        const scroll = clonedEl.querySelector('.chart-grid-scroll');
        if (scroll) {
          scroll.style.width = `${captureW}px`;
          scroll.style.minWidth = `${captureW}px`;
          scroll.style.overflow = 'visible';
          scroll.style.overflowX = 'visible';
        }
      },
    });
  } finally {
    restoreLayout();
    window.dispatchEvent(new Event('resize'));
  }
}
